import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAskRecordSourceNavigation from '../../AskRecord/useAskRecordSourceNavigation';
import { requestTimelineEntryFocus } from '../../AskRecord/askRecordSourceNavigation';

const useMobileDashboardNavigation = ({
  activeChild,
  childShellTarget,
  clearTimelineFilters,
  onChildShellTargetHandled,
  rootRef,
  setSelectedDate,
  timelineRef,
  toolsRef,
}) => {
  const navigate = useNavigate();
  const [timelineFocusEntryId, setTimelineFocusEntryId] = useState(null);
  const [activeShellItem, setActiveShellItem] = useState(childShellTarget === 'tools' ? 'tools' : 'today');

  const resetTimelineFocus = useCallback(() => {
    setTimelineFocusEntryId(null);
  }, []);

  useEffect(() => {
    if (!childShellTarget || childShellTarget === 'profile') {
      return;
    }

    const nextShellItem = childShellTarget === 'tools' ? 'tools' : 'today';
    setActiveShellItem(nextShellItem);
    const targetRef = nextShellItem === 'tools' ? toolsRef : rootRef;
    requestAnimationFrame(() => {
      targetRef.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'start',
      });
      onChildShellTargetHandled?.();
    });
  }, [childShellTarget, onChildShellTargetHandled, rootRef, toolsRef]);

  const handleOpenAskSource = useAskRecordSourceNavigation({
    beforeNavigate: () => {
      clearTimelineFilters();
    },
    setSelectedDate,
    setTimelineFocusEntryId,
    timelineRef,
  });

  const handleReviewSourceTimeline = useCallback((target = {}) => {
    if (target?.date) {
      setSelectedDate(target.date);
    }

    clearTimelineFilters();
    requestTimelineEntryFocus(setTimelineFocusEntryId, target?.entryId);
    window.setTimeout(() => {
      timelineRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }, 0);
  }, [clearTimelineFilters, setSelectedDate, timelineRef]);

  const handleOpenPatternsPage = useCallback((child) => {
    if (!child?.id) {
      return;
    }

    navigate('/patterns', { state: { selectedChildId: child.id } });
  }, [navigate]);

  const handleOpenCareKnowledge = useCallback((child) => {
    if (!child?.id) {
      return;
    }

    navigate('/care-knowledge', { state: { childId: child.id } });
  }, [navigate]);

  const handleOpenIepJourney = useCallback((child) => {
    if (child?.id) navigate(`/children/${child.id}/journeys/iep-meeting`);
  }, [navigate]);

  const handleOpenCustomPrepare = useCallback((child) => {
    if (child?.id) navigate('/prepare', { state: { selectedChildId: child.id } });
  }, [navigate]);

  const handleChildShellNavSelect = useCallback((itemId) => {
    if (itemId === 'home') {
      navigate('/home', { state: { selectedChildId: activeChild?.id || null } });
      return;
    }

    if (itemId === 'today') {
      setActiveShellItem('today');
      rootRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      return;
    }

    if (itemId === 'profile') {
      if (activeChild?.id) {
        navigate('/care-profile', { state: { childId: activeChild.id } });
      }
      return;
    }

    if (itemId === 'tools') {
      setActiveShellItem('tools');
      toolsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      return;
    }

  }, [activeChild, navigate, rootRef, toolsRef]);

  return {
    activeShellItem,
    handleChildShellNavSelect,
    handleOpenAskSource,
    handleOpenCareKnowledge,
    handleOpenCustomPrepare,
    handleOpenIepJourney,
    handleOpenPatternsPage,
    handleReviewSourceTimeline,
    resetTimelineFocus,
    timelineFocusEntryId,
  };
};

export default useMobileDashboardNavigation;
